import { RotateCcw, Printer } from 'lucide-react'
import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { salesService, SaleDetail } from '../../../services/sales.service'
import { useQuery } from '@tanstack/react-query'
import { DiscreteLoader } from '../../ui/discrete-loader'
import { TicketPreviewModal } from '../../pos/modals/TicketPreviewModal'

interface SaleDetailModalProps {
  isOpen: boolean
  onClose: () => void
  sale: { id: string | number } | null
  onOpenReturnModal: (sale: any, type: 'partial' | 'total') => void
  onOpenCancelModal: (sale: any) => void
  autoPrint?: boolean
  onPrintComplete?: () => void
}

export function SaleDetailModal({ isOpen, onClose, sale, onOpenReturnModal, onOpenCancelModal, autoPrint = false, onPrintComplete }: SaleDetailModalProps) {
  const [ticketCopies, setTicketCopies] = useState(1)
  const [showPrintPreview, setShowPrintPreview] = useState(false)

  const { data: saleDetail, isLoading } = useQuery({
    queryKey: ['saleDetail', sale?.id],
    queryFn: () => salesService.getSale(sale!.id),
    enabled: !!sale?.id && isOpen,
  })

  useEffect(() => {
    if (isOpen && saleDetail && autoPrint && !isLoading) {
      setShowPrintPreview(true)
      onPrintComplete?.()
    }
  }, [isOpen, saleDetail, autoPrint, isLoading])

  useEffect(() => {
    if (isOpen) {
      setTicketCopies(1)
    }
  }, [isOpen])

  if (!isOpen || !sale) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="border-b border-slate-200 px-5 py-4 pr-14">
          <div className="flex flex-wrap items-center justify-between gap-3 w-full">
            <div className="flex flex-wrap items-center gap-3">
              <DialogTitle>Detalle de Venta</DialogTitle>
            </div>
            {saleDetail && !isLoading && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={ticketCopies || ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value)
                    setTicketCopies(isNaN(val) ? 0 : val)
                  }}
                  onBlur={() => {
                    if (!ticketCopies || ticketCopies < 1) setTicketCopies(1)
                  }}
                  className="h-9 w-16 text-center"
                  title="Copias a imprimir"
                />
                <Button variant="default" size="sm" onClick={() => setShowPrintPreview(true)}>
                  <Printer className="mr-2 h-4 w-4" />
                  Imprimir
                </Button>
              </div>
            )}
          </div>
          <DialogDescription className="sr-only">
            Información completa de la venta y productos vendidos
          </DialogDescription>
        </DialogHeader>
        
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 flex justify-center">
               <DiscreteLoader text="Cargando detalles de la venta..." />
            </div>
          ) : saleDetail ? (
          <div className="space-y-6">
            {/* Sale Info */}
            <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 p-4">
              <div>
                <p className="text-sm text-slate-600">Ticket:</p>
                <p className="font-semibold text-slate-900">{saleDetail.ticket_number}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Fecha y Hora:</p>
                <p className="font-semibold text-slate-900">
                  {new Date(saleDetail.completed_at.endsWith('Z') ? saleDetail.completed_at : saleDetail.completed_at.replace(' ', 'T') + 'Z').toLocaleString('es-MX', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Cliente:</p>
                <p className="font-semibold text-slate-900">{saleDetail.customer?.name || 'Mostrador'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Vendedor:</p>
                <p className="font-semibold text-slate-900">{saleDetail.seller?.name} {saleDetail.seller?.last_name || ''}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Sucursal:</p>
                <p className="font-semibold text-slate-900">{saleDetail.sucursal?.name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Método de Pago:</p>
                <p className="font-semibold text-slate-900">{saleDetail.payment?.method?.name || 'N/A'}</p>
              </div>
            </div>

            {/* Items */}
            <div>
              <h4 className="mb-3 font-medium text-slate-900">Productos</h4>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">Producto</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">Cantidad</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">Precio</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">Desc.</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">Subtotal</th>
                      {saleDetail.status !== 'COMPLETED' && (
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">Devuelto</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {saleDetail.items.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-2">{item.product_name}</td>
                        <td className="px-4 py-2">{item.quantity}</td>
                        <td className="px-4 py-2">${Number(item.unit_price).toFixed(2)}</td>
                        <td className="px-4 py-2">${Number(item.discount_amount).toFixed(2)}</td>
                        <td className="px-4 py-2 font-medium">${Number(item.subtotal).toFixed(2)}</td>
                        {saleDetail.status !== 'COMPLETED' && (
                          <td className="px-4 py-2">
                            {Number(item.returned_quantity) > 0 && (
                              <Badge className="bg-red-100 text-red-700">{item.returned_quantity}</Badge>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="space-y-2 border-t border-slate-200 pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal:</span>
                <span className="font-medium">${Number(saleDetail.amounts?.subtotal || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">IVA (16%):</span>
                <span className="font-medium">${Number(saleDetail.amounts?.tax_total || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-2">
                <span className="font-semibold text-slate-900">Total:</span>
                <span className="text-xl font-semibold text-slate-900">${Number(saleDetail.amounts?.total || 0).toFixed(2)}</span>
              </div>
              {saleDetail.returns && saleDetail.returns.length > 0 && (
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="font-medium text-orange-600">Total Reembolsado:</span>
                  <span className="text-lg font-medium text-orange-600">
                    -${saleDetail.returns.reduce((acc: number, ret: any) => acc + Number(ret.amounts?.total_refund || 0), 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Return Info */}
            {saleDetail.returns && saleDetail.returns.length > 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 space-y-3">
                <p className="text-sm font-medium text-yellow-900">Información de Devoluciones</p>
                {saleDetail.returns.map((ret, idx) => (
                  <div key={ret.id || idx} className="text-sm text-yellow-800 border-l-2 border-yellow-400 pl-3">
                    <p>Fecha: {ret.processed_at ? new Date(ret.processed_at.endsWith('Z') ? ret.processed_at : ret.processed_at.replace(' ', 'T') + 'Z').toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short'}) : 'Pendiente'}</p>
                    <p>Motivo: {ret.reason}</p>
                    <p>Monto Reembolsado: ${Number(ret.amounts?.total_refund || 0).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              {saleDetail.actions?.can_cancel && (
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => {
                    onClose()
                    onOpenCancelModal({
                      id: saleDetail.id,
                      paymentMethod: saleDetail.payment?.method?.name || '',
                      ticketNumber: saleDetail.ticket_number,
                      invoiced: (saleDetail as any).invoiced,
                      cfdi_id: (saleDetail as any).cfdi_id ?? null,
                      cfdi_cancelled_at: (saleDetail as any).cfdi_cancelled_at ?? null,
                    })
                  }}
                >
                  Cancelar Venta Completa
                </Button>
              )}
              {saleDetail.actions?.can_return && (
                <>
                  <Button
                    onClick={() => {
                      onClose()
                      onOpenReturnModal(saleDetail, 'partial')
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Devolución Parcial
                  </Button>
                  <Button
                    onClick={() => {
                      onClose()
                      onOpenReturnModal(saleDetail, 'total')
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Devolución Total
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
           <div className="py-10 text-center text-slate-500">
             Ocurrió un error al cargar la venta.
           </div>
        )}
        </div>
      </DialogContent>

      {showPrintPreview && saleDetail && (
        <TicketPreviewModal
          open={showPrintPreview}
          onOpenChange={setShowPrintPreview}
          isPrintOnly={true}
          ticketNumber={saleDetail.ticket_number}
          branchName={saleDetail.sucursal?.name || '—'}
          cashRegisterName={saleDetail.cash_register?.name || '—'}
          cashierName={`${saleDetail.seller?.name || ''} ${saleDetail.seller?.last_name || ''}`.trim() || 'Admin'}
          customerType={(saleDetail as any).customer_id ? 'registered' : 'general'}
          selectedCustomer={(saleDetail as any).customer_id}
          customers={(saleDetail as any).customer ? [{ id: (saleDetail as any).customer.id, name: (saleDetail as any).customer.name }] : []}
          saleType={(saleDetail as any).is_delivery ? 'delivery' : 'regular'}
          deliveryDetails={{
             deliveryUserId: (saleDetail as any).delivery?.delivery_user_id || '',
             deliveryPerson: (saleDetail as any).delivery?.deliveryUser?.name || '',
             customerPhone: (saleDetail as any).customer_phone || '',
             customerAddress: (saleDetail as any).delivery?.address || '',
             observations: (saleDetail as any).delivery?.notes || '',
             cost: Number(saleDetail.amounts?.delivery_fee || 0)
          } as any}
          cart={saleDetail.items.map((item: any) => {
            const quantity = Number(item.quantity)
            const subtotalWithoutTax = Number(item.subtotal)
            const discountWithoutTax = Number(item.discount_amount)
            const taxAmount = Number(item.tax_amount || 0)
            
            const taxableAmount = subtotalWithoutTax - discountWithoutTax
            const vatRate = (taxableAmount > 0 && taxAmount > 0) ? (taxAmount / taxableAmount) : 0
            const vatMultiplier = 1 + vatRate
            
            const regularUnitPriceWithTax = (subtotalWithoutTax / quantity) * vatMultiplier
            const appliedUnitPriceWithTax = ((subtotalWithoutTax - discountWithoutTax) / quantity) * vatMultiplier
            
            return {
             id: item.id,
             product_id: item.product_id,
             name: item.product_name || item.product?.name,
             quantity: quantity,
             price: Number(item.unit_price),
             stock: 0,
             type: 'product',
             amounts: {
               regular_unit_total: regularUnitPriceWithTax,
               applied_unit_total: appliedUnitPriceWithTax,
               total: appliedUnitPriceWithTax * quantity
             },
             pricing: {
               discount_percentage: discountWithoutTax > 0 ? ((discountWithoutTax / subtotalWithoutTax) * 100).toFixed(0) : 0
             }
            } as any
          })}
          subtotal={Number(saleDetail.amounts?.subtotal || 0)}
          discountTotal={Number(saleDetail.amounts?.discount_total || 0)}
          tax={Number(saleDetail.amounts?.tax_total || 0)}
          deliveryCost={Number(saleDetail.amounts?.delivery_fee || 0)}
          pointsDiscount={0}
          total={Number(saleDetail.amounts?.total || 0)}
          pointsEarned={0}
          pointsToUse={0}
          selectedPaymentMethod={(saleDetail as any).payments?.[0]?.paymentMethod?.code || saleDetail.payment?.method?.name || 'cash'}
          selectedBank={(saleDetail as any).payments?.[0]?.bank_name || ''}
          reference={(saleDetail as any).payments?.[0]?.reference || ''}
          cashReceived={String((saleDetail as any).payments?.[0]?.cash_received_amount || saleDetail.amounts?.total)}
          change={Number((saleDetail as any).payments?.[0]?.change_amount || 0)}
          requiresInvoice={(saleDetail as any).requires_invoice}
          ticketCopies={ticketCopies}
          completeSale={() => setShowPrintPreview(false)}
          setShowPaymentModal={() => setShowPrintPreview(false)}
        />
      )}
    </Dialog>
  )
}
